/**
 * Partage de panier « Partager mon panier ».
 *
 * Côté expéditeur : au clic sur [data-ref="cart-share-button"], on envoie le
 * snapshot du panier (/cart.js réduit à id/quantité/propriétés) au backend qui
 * répond un token court. Le lien /cart?shared-cart=TOKEN part dans la feuille
 * de partage native (mobile) ou dans le presse-papier (desktop).
 *
 * Côté destinataire : au chargement avec ?shared-cart=TOKEN, on récupère les
 * lignes et on les rejoue via /cart/add.js UNE PAR UNE (une variante épuisée ne
 * doit pas bloquer les autres), puis on recharge /cart sans le paramètre. Le
 * token consommé est mémorisé en sessionStorage pour ne pas dupliquer les
 * articles au retour arrière.
 */
const SHARED_CART_ENDPOINT = `${
  localStorage.getItem('lfc-backend-override') || 'https://template.lesfrereschaussettes.com'
}/shared-cart`;

const APPLIED_KEY_PREFIX = 'lfc-shared-cart-applied:';
const WARNING_KEY = 'lfc-shared-cart-warning';

// Attend l'enregistrement du composant : les toasts du flux destinataire
// partent dès le chargement de la page, avant toast-notification.js (deferred).
const toast = (type, message) =>
  customElements.whenDefined('toast-notification').then(() => {
    requestAnimationFrame(() =>
      document.dispatchEvent(new CustomEvent('toast:open', { detail: { type, message } })),
    );
  });

/* ——— Expéditeur ——— */

let shareInFlight = false;

/** Snapshot du panier → token backend → URL à partager. */
const buildShareUrl = async () => {
  const cart = await fetch('/cart.js').then((res) => res.json());
  const items = (cart.items || []).map((item) => ({
    id: item.variant_id || item.id,
    quantity: item.quantity,
    properties: item.properties || undefined,
  }));
  if (!items.length) throw Object.assign(new Error('panier vide'), { emptyCart: true });

  const response = await fetch(SHARED_CART_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const { token } = await response.json();
  return `${location.origin}/cart?shared-cart=${token}`;
};

/**
 * Copie initiée PENDANT le geste utilisateur : l'activation transitoire expire
 * durant les fetchs, un writeText après coup est refusé (NotAllowedError,
 * Safari surtout). ClipboardItem accepte une promesse créée de façon synchrone
 * dans le handler de clic — writeText en repli pour les navigateurs sans
 * ClipboardItem. Doit être appelé sans await préalable.
 */
const copyWhenReady = (urlPromise) => {
  if (window.ClipboardItem && navigator.clipboard?.write) {
    return navigator.clipboard.write([
      new ClipboardItem({
        'text/plain': urlPromise.then((url) => new Blob([url], { type: 'text/plain' })),
      }),
    ]);
  }
  return urlPromise.then((url) => navigator.clipboard.writeText(url));
};

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-ref="cart-share-button"]');
  if (!button || shareInFlight) return;

  shareInFlight = true;
  button.classList.add('is-loading');

  // Tout ce qui touche au presse-papier doit partir de ce tick synchrone.
  const urlPromise = buildShareUrl();
  const onMobile = /Android|iPhone|iPad|Mobile/i.test(navigator.userAgent);

  const share = async () => {
    if (onMobile && navigator.share) {
      const url = await urlPromise;
      try {
        await navigator.share({ title: 'Mon panier Les Frères Chaussettes', url });
      } catch (error) {
        if (error?.name === 'AbortError') return; // partage annulé par l'utilisateur
        window.prompt('Copiez ce lien :', url); // feuille de partage refusée → copie manuelle
      }
      return;
    }
    try {
      await copyWhenReady(urlPromise);
      toast('success', 'Lien copié ! Envoyez-le à qui vous voulez.');
    } catch {
      window.prompt('Copiez ce lien :', await urlPromise); // presse-papier refusé → copie manuelle
    }
  };

  share()
    .catch((error) => {
      if (error?.emptyCart) {
        toast('info', 'Votre panier est vide.');
        return;
      }
      console.error('Partage du panier impossible :', error);
      toast('error', 'Impossible de créer le lien, réessayez.');
    })
    .finally(() => {
      shareInFlight = false;
      button.classList.remove('is-loading');
    });
});

/* ——— Destinataire ——— */

const stripSharedCartParam = () => {
  const url = new URL(location.href);
  url.searchParams.delete('shared-cart');
  history.replaceState(null, '', url.toString());
};

const applySharedCart = async () => {
  const token = new URLSearchParams(location.search).get('shared-cart');
  if (!token || !/^[A-Za-z0-9_-]{6,16}$/.test(token)) return;

  if (sessionStorage.getItem(APPLIED_KEY_PREFIX + token)) {
    stripSharedCartParam();
    return;
  }

  try {
    const response = await fetch(`${SHARED_CART_ENDPOINT}/${token}`);
    if (response.status === 404) {
      stripSharedCartParam();
      toast('error', 'Ce lien de panier a expiré.');
      return;
    }
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const { items } = await response.json();

    let added = 0;
    for (const item of items) {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [item] }),
      });
      if (res.ok) added += 1;
    }

    sessionStorage.setItem(APPLIED_KEY_PREFIX + token, '1');
    if (!added) {
      stripSharedCartParam();
      toast('error', 'Les articles de ce panier ne sont plus disponibles.');
      return;
    }
    if (added < items.length) sessionStorage.setItem(WARNING_KEY, '1');
    location.replace('/cart');
  } catch (error) {
    console.error('Récupération du panier partagé impossible :', error);
    stripSharedCartParam();
    toast('error', 'Impossible de récupérer ce panier, réessayez.');
  }
};

if (sessionStorage.getItem(WARNING_KEY)) {
  sessionStorage.removeItem(WARNING_KEY);
  toast('warning', 'Certains articles du panier partagé ne sont plus disponibles.');
}

applySharedCart();
